import React, { useEffect, useState } from 'react';
import DataList from './DataList';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

const MedicalContractList = () => {
  const { token } = useAuth();
  const [kitOptions, setKitOptions] = useState([]);
  const [testOptions, setTestOptions] = useState([]);

  const testTypeOptions = [
    { value: 'Lab', label: 'Lab' },
    { value: 'Radiology', label: 'Radiology' },
  ];

  useEffect(() => {
    const fetchLabTestOptions = async () => {
      try {
        const [kitsResponse, testsResponse] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/labTests?is_active=1&is_kit=1`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/lab/labTests?is_active=1&is_kit=0`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        setKitOptions(
          kitsResponse.data.map((kit) => ({
            value: kit.id,
            label: `${kit.test_name}${kit.lab_type ? ` (${kit.lab_type})` : ''}`,
          }))
        );

        setTestOptions(
          testsResponse.data.map((test) => ({
            value: test.id,
            label: `${test.test_name}${test.category ? ` (${test.category})` : ''}`,
          }))
        );
      } catch (error) {
        console.error('Error loading kit/test options for medical assessments:', error);
      }
    };

    fetchLabTestOptions();
  }, [token]);

  const getCriteriaCount = (value) => {
    if (Array.isArray(value)) return value.length;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.length : 0;
      } catch (error) {
        return 0;
      }
    }
    return 0;
  };

  const normalizeSubformRows = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          return {
            id: item.id ?? item.value ?? '',
            test_type: item.test_type === 'Audiometry' ? '' : (item.test_type || ''),
          };
        }
        return { id: item, test_type: '' };
      })
      .filter((item) => item.id !== '' && item.id !== null && item.id !== undefined);
  };

  const apiConfig = {
    title: 'Medical Assessments',
    path: '/medical-contract-list',
    fetchEndpoint: '/hospital-portal/api/medicalreports/medical-contract/',
    createEndpoint: '/hospital-portal/api/medicalreports/medical-contract/form',
    updateEndpoint: '/hospital-portal/api/medicalreports/medical-contract/form',
    deleteEndpoint: '/hospital-portal/api/medicalreports/medical-contract/form',
    gcBucketName: 'sh-medicals-upload-us',
    defaultSortFieldId: 'id',
    defaultSortAsc: false,
    allowAdd: true,
    allowEdit: true,
    expandableRows: 'Off',
    dropdownFields: [
      {
        name: 'customer_name',
        endpoint: '/hospital-portal/api/customers?status=Active',
        valueField: 'customer_name',
        labelField: 'customer_name',
      },
    ],
    onBeforeAdd: () => ({
      included_kit_ids: [],
      included_test_ids: [],
      includes_audiometry: false,
    }),
    cloneOmitFields: ['assessment_code', 'employee_list_file_path'],
    cloneTransform: (clonedRecord) => ({
      ...clonedRecord,
      assessment_code: '',
      employee_list_file_path: null,
    }),
    bulkActions: [
      { name: 'Clone', type: 'clone' },
      { name: 'Generate Progress Report', endpoint: '/hospital-portal/api/medicalreports/medical-contract/generate-report' },
      {
        name: 'Download All',
        endpoint: '/hospital-portal/api/medicalreports/medical-contract/download-report',
        download: true,
        downloadFileName: 'medical-assessment-all.xlsx',
        payload: { reportMode: 'all' },
      },
      {
        name: 'Download Complete',
        endpoint: '/hospital-portal/api/medicalreports/medical-contract/download-report',
        download: true,
        downloadFileName: 'medical-assessment-complete.xlsx',
        payload: { reportMode: 'complete' },
      },
      {
        name: 'Download Incomplete',
        endpoint: '/hospital-portal/api/medicalreports/medical-contract/download-report',
        download: true,
        downloadFileName: 'medical-assessment-incomplete.xlsx',
        payload: { reportMode: 'incomplete' },
      },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'number', identifier: true, disabled: true, tab: 'Main' },
      { name: 'assessment_code', label: 'Assessment Code', type: 'text', required: true, tab: 'Main' },
      { name: 'assessment_name', label: 'Assessment Name', type: 'text', required: true, tab: 'Main' },
      { name: 'customer_name', label: 'Customer Name', type: 'dropdown', required: true, tab: 'Main' },
      { name: 'start_date', label: 'Start Date', type: 'date', required: true, tab: 'Main' },
      { name: 'status', label: 'Status', type: 'dropdown', required: true, defaultValue: 'Active', tab: 'Main' },
      { name: 'includes_audiometry', label: 'Includes Audiometry', type: 'checkbox', required: false, defaultValue: false, tab: 'Main' },
      {
        name: 'included_kit_ids',
        label: 'Included Kits',
        type: 'subform',
        required: false,
        tab: 'Test Criteria',
        fixedRows: false,
        onInitialize: normalizeSubformRows,
        subFields: [
          {
            name: 'id',
            label: 'Kit',
            type: 'dropdown',
            width: '420px',
            required: true,
            options: kitOptions,
          },
          {
            name: 'test_type',
            label: 'Test Type',
            type: 'dropdown',
            width: '220px',
            required: true,
            options: testTypeOptions,
          },
        ],
      },
      {
        name: 'included_test_ids',
        label: 'Included Tests',
        type: 'subform',
        required: false,
        tab: 'Test Criteria',
        fixedRows: false,
        onInitialize: normalizeSubformRows,
        subFields: [
          {
            name: 'id',
            label: 'Test',
            type: 'dropdown',
            width: '420px',
            required: true,
            options: testOptions,
          },
          {
            name: 'test_type',
            label: 'Test Type',
            type: 'dropdown',
            width: '220px',
            required: true,
            options: testTypeOptions,
          },
        ],
      },
      {
        name: 'employee_list_file_path',
        label: 'Employee List File (Reference)',
        type: 'file',
        tab: 'Attachments',
        accept: '.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
      },
      { name: 'notes', label: 'Notes', type: 'textarea', required: false, tab: 'Attachments', maxRows: 5 },
      { name: 'access_accounts', label: 'Access Accounts', type: 'textarea', required: false, tab: 'Legacy', maxRows: 5 },

      // Backward compatibility fields
      { name: 'batch', label: 'Legacy Batch', type: 'text', required: false, tab: 'Legacy' },
      { name: 'total_price', label: 'Legacy Total Price', type: 'number', tab: 'Legacy', required: false },
      { name: 'employee_count', label: 'Legacy Employee Count', type: 'number', tab: 'Legacy', required: false },
      { name: 'columns_config', label: 'Legacy Column Configuration', type: 'textarea', maxRows: 8, tab: 'Legacy', allowJson: true },
      { name: 'fields_config', label: 'Legacy Field Configuration', type: 'textarea', maxRows: 8, tab: 'Legacy', allowJson: true },
    ],
    staticDropdownFields: [
      {
        name: 'status',
        options: [
          { id: 'Active', name: 'Active' },
          { id: 'Closed', name: 'Closed' },
          { id: 'On Hold', name: 'On Hold' },
        ],
      },
    ],
    tabs: [
      { name: 'Main', roles: 'all', default: true },
      { name: 'Test Criteria', roles: 'all' },
      { name: 'Attachments', roles: 'all' },
      { name: 'Legacy', roles: 'Admin' },
    ],
  };

  const columns = [
    { id: 'id', name: 'ID', selector: (row) => row.id, type: 'integer', sortable: true, identifier: true, width: '50px' },
    { name: 'Assessment Code', selector: (row) => row.assessment_code, sortable: true, filterable: true, filterSelector: 'assessment_code' },
    { name: 'Assessment Name', selector: (row) => row.assessment_name, sortable: true, filterable: true, filterSelector: 'assessment_name' },
    { name: 'Customer Name', selector: (row) => row.customer_name, sortable: true, filterable: true, filterSelector: 'customer_name' },
    { name: 'Start Date', selector: (row) => row.start_date, type: 'date', sortable: true },
    {
      name: 'Kits',
      selector: (row) => getCriteriaCount(row.included_kit_ids),
      sortable: true,
    },
    {
      name: 'Tests',
      selector: (row) => getCriteriaCount(row.included_test_ids),
      sortable: true,
    },
    { name: 'Status', selector: (row) => row.status, sortable: true },
  ];

  return <DataList apiConfig={apiConfig} columns={columns} />;
};

export default MedicalContractList;
